import type { RecordOf } from 'immutable';
import { call, delay, put, race, select, take, takeEvery, takeLatest } from 'redux-saga/effects';

import { preferencesActions } from 'app-components/flux/preferences';
import { isDemoMode } from 'app-components/utils/core';

import {
  getHostConfig,
  getHttpStatus,
  getLogger,
  httpClientInterface,
  requireEnvironmentConfig,
  requireHostConfig,
  tracker,
} from '../../utils/core';
import store from '../../utils/redux-store';
import { resourceSagaUtils, resourceStoreTypes } from '../core';
import type { BaseResource } from '../core/resourceStoreTypes';
import { datasetsActions } from '../datasets';
import * as actions from './authActions';
import { createRefreshTokenRequest } from './authRefreshTokenRequest';
import * as authSelectors from './authSelectors';
import * as authTypes from './authTypes';

const log = getLogger('flux/auth/sagas');

// The token endpoint takes the exchange request and answers with the new tokens.
type TokenExchange = ReturnType<typeof actions.exchangeRefreshToken>['payload'];
type TokenResponse = authTypes.AuthTokenDataProps & BaseResource;

// if multiple exchange refresh token requests triggered simultaneously then we
// - execute only the first one (exchangeRefreshTokenInProgress)
// - queue the rest of them (exchangeRefreshTokenWaitList)
// - once exchange complete we resolve/reject promises for queued actions
let exchangeRefreshTokenInProgress = false;
const exchangeRefreshTokenWaitList: ReturnType<typeof actions.exchangeRefreshToken>[] = [];
export function* exchangeRefreshTokenActionWatcher() {
  yield takeEvery(actions.exchangeRefreshToken, function* exchangeRefreshToken(action) {
    if (exchangeRefreshTokenInProgress) {
      exchangeRefreshTokenWaitList.push(action);
    } else {
      let useRefreshTokenAction: ReturnType<typeof actions.exchangeRefreshToken> | undefined;
      if (action.payload) {
        useRefreshTokenAction = action;
      } else {
        const refreshToken: string | undefined = yield select(authSelectors.getRefreshToken);
        if (refreshToken) {
          useRefreshTokenAction = actions.exchangeRefreshToken(
            createRefreshTokenRequest({
              refreshToken,
              redirectUri: `${(getHostConfig() || requireHostConfig('localhost')).redirect_uri}`,
              clientId: `${requireEnvironmentConfig().client_id}`,
              clientSecret: `${requireEnvironmentConfig().client_secret}`,
            }),
            {
              reject: (error) => {
                // assert(false, 'refresh token failure [useRefreshTokenActionWatcher] - force logout');
                // store.dispatch(actions.authLogout({ reason: 'AUTH_LOGOUT_UNAUTHORIZED' }));
                const status = getHttpStatus(error);
                if (status && [400, 401, 406, 500].includes(status)) {
                  // TODO: Don't use global store here
                  store?.dispatch(actions.invalidateRefreshToken(undefined, { context: 'auth refresh failed' }));
                }
              },
              ...action.meta,
            },
          );
        }
      }

      if (useRefreshTokenAction?.payload) {
        const config = resourceStoreTypes.createTypedQcsSyncResourcesConfigFactory<TokenResponse, TokenExchange>()({
          resourceName: 'token',
          resourceBaseUrl: '/oauth/token',
          successAction: actions.exchangeRefreshTokenSuccess,
          failureAction: actions.exchangeRefreshTokenFailure,
          axiosConfig: {
            headers: {
              Authorization: null,
            },
          },
          transformResourceWithResponse: (resource, response) => response.data,
          handleRetry: false,
          ignoreError: true,
        });
        exchangeRefreshTokenInProgress = true;
        yield call(
          resourceSagaUtils.qcsSyncCreateResource<TokenResponse, TokenExchange>,
          config,
          useRefreshTokenAction,
        );
      } else {
        yield put(actions.exchangeRefreshTokenFailure('Valid Refresh Token not found'));
      }
    }
  });
}

export function* exchangeRefreshTokenSuccessActionWatcher() {
  yield takeEvery(actions.exchangeRefreshTokenSuccess, function* exchangeRefreshTokenSuccess(action) {
    // Track npsRequested if survey is requested by QCS.
    if (action.payload?.surveyRequested) {
      tracker.track(tracker.events.npsRequested);
    }
    exchangeRefreshTokenInProgress = false;
    const waitList = exchangeRefreshTokenWaitList.splice(0);
    waitList.forEach((waitAction) => {
      waitAction.meta?.resolve?.(action.payload);
    });
  });
}

export function* exchangeRefreshTokenFailureActionWatcher() {
  yield takeEvery(actions.exchangeRefreshTokenFailure, function* exchangeRefreshTokenFailure(action) {
    exchangeRefreshTokenInProgress = false;
    const waitList = exchangeRefreshTokenWaitList.splice(0);
    waitList.forEach((waitAction) => {
      waitAction.meta?.reject?.(action.payload);
    });
  });
}

function SessionStateError(this: any, response: any) {
  this.response = response;
}
function* retrieveOauthToken({ payload: code, meta }: ReturnType<typeof actions.getOAuthToken>) {
  try {
    log.log('Retrieving oAuth Token');
    const requestUrl = `${requireEnvironmentConfig().services_url}/oauth/token`;

    const requestBody = {
      clientId: requireEnvironmentConfig().client_id,
      clientSecret: requireEnvironmentConfig().client_secret,
      grantType: 'authorization_code',
      code,
      redirectUri: meta?.props?.redirectUri ?? requireHostConfig().redirect_uri,
    };

    let requestConfig: any = {
      headers: {
        Authorization: null,
      },
    };

    const sessionState = meta?.props?.sessionState;
    log.debug('local auth session state: ', sessionState);
    if (assert(isDemoMode() || sessionState, 'no auth session state found #bug-trap')) {
      requestConfig = {
        ...requestConfig,
        headers: {
          ...requestConfig.headers,
          'qcs-session-state': sessionState,
          'qcs-session-state-provided': 'true',
        },
      };
    }

    const response: { data: any; config: any } = yield call(
      httpClientInterface.send,
      'POST',
      requestUrl,
      requestBody,
      requestConfig.headers,
    );
    const { data, config } = response;
    if (config && config.headers && config.headers['qcs-session-state']) {
      const sessionStateHeaderValue = config.headers['qcs-session-state'];
      if (sessionStateHeaderValue !== sessionState) {
        throw new (SessionStateError as any)({
          data: {
            errors: [
              {
                httpStatus: 401,
                detail: 'QCS Unauthorized Access - Session state mismatch detected',
                code: 'QCS-0401-16',
              },
            ],
          },
          message: 'session state mismatch',
        });
      }
    }
    if (data.accessToken) {
      const authTokenData = new authTypes.AuthTokenData({
        accessToken: data.accessToken,
        accessTokenExpired: data.accessTokenExpired,
        refreshToken: data.refreshToken,
        refreshTokenExpired: data.refreshTokenExpired,
        scope: data.scope,
        surveyRequested: isDemoMode() ? false : data.surveyRequested,
      });

      yield put(actions.getOAuthTokenSuccess(authTokenData));
      // Track npsRequested if survey is requested by QCS.
      if (data.surveyRequested) {
        tracker.track(tracker.events.npsRequested);
      }
      tracker.resetActiveTimestamp();
    } else {
      yield put(actions.getOAuthTokenFailure(response));
    }
  } catch (error: any) {
    log.error('Continued after catching error', error);

    // {"response":{"data":{"errors":[{"httpStatus":401,"detail":"QCS Unauthorized Access - Session state mismatch detected","code":"QCS-0401-16"}]},"message":"session state mismatch"}}
    try {
      const exceptionData = error.response?.data?.errors?.[0];
      if (exceptionData?.httpStatus === 401 && exceptionData?.code === 'QCS-0401-16') {
        // This errors out on mobile. We should update this to do something different
        // biome-ignore lint/suspicious/noTsIgnore: only some consumers type window, so @ts-expect-error would fail in the others
        // @ts-ignore
        (window as any).location.href = `${requireHostConfig().redirect_uri}?session_mismatch=1`;
      }
    } catch (exception: any) {
      assert(false, exception);
    }

    yield put(actions.getOAuthTokenFailure(error));
  }
}

function* retrieveOauthTokenWatcher() {
  yield takeLatest(actions.getOAuthToken, retrieveOauthToken);
}

function* createDatasetSuccessActionWatcher() {
  yield takeEvery(datasetsActions.createDatasetSuccess, function* createDatasetSuccess(action) {
    const datasetId: string | undefined = yield select(authSelectors.getDatasetId);
    if (action.payload.id === datasetId) {
      yield put(datasetsActions.datasetPoll({ id: action.payload.id }));
    }
  });
}

function* authSelectDatasetActionWatcher() {
  yield takeEvery(actions.authSelectDataset, function* authSelectDataset(action) {
    const { dataset, reload = true, location, userChoice = false } = action.payload;

    if (dataset?.id) {
      if (userChoice) {
        // TODO: replace with prefs v2 once resolved QCS-17004
        yield put(
          preferencesActions.setPreference({
            section: 'shared',
            group: 'user',
            preference: {
              webApp: {
                selectedDatasetId: dataset.id,
              },
            },
          }),
        );
        yield race([
          take(preferencesActions.setPreferenceSuccess),
          take(preferencesActions.setPreferenceFailure),
          delay(3000),
        ]);
        // yield put(preferencesV2Actions.updatePreferenceOverride(preferencesV2Types.mkPreferenceV2({
        //   id: 'selectedDatasetId',
        //   value: dataset.id,
        // })));
        // yield race([
        //   take(preferencesV2Actions.updatePreferenceOverrideSuccess),
        //   take(preferencesV2Actions.updatePreferenceOverrideFailure),
        //   delay(3000),
        // ]);
      }

      const prevAuthSession: RecordOf<authTypes.AuthStoreProps> = yield select(authSelectors.getAuthSession);
      const authSession = prevAuthSession.merge({
        datasetId: dataset?.id ?? null,
        datasetPlatform: dataset?.platform ?? null,
        datasetSubscriptionTier: dataset?.subscriptionTier ?? null,
        datasetCreatedByClientId: dataset?.createdByClientId ?? null,
        datasetCreatedAt: dataset?.createdAt ?? null,
      });
      yield put(
        actions.authUpdated({ authSession, reload, location, prevAuthSession }, { context: 'authSelectDataset' }),
      );
    }

    if (dataset?.shared) {
      tracker.track(tracker.events.sharedDatasetAccessed);
    }
    tracker.resetActiveTimestamp();
  });
}

function* getOAuthTokenFailureWatcher() {
  yield takeEvery(actions.getOAuthTokenFailure, function* getOAuthTokenFailure() {
    yield put(actions.authLogout({ reason: 'AUTH_LOGOUT_UNAUTHORIZED' }, { context: 'getOAuthTokenFailure' }));
  });
}

function* watchPersistAuth() {
  yield takeEvery(
    [
      actions.authLogin,
      actions.getOAuthTokenSuccess,
      actions.invalidateAccessToken,
      actions.updateAuth,
      actions.invalidateRefreshToken,
    ],
    function* () {
      yield put(actions.persistAuthSession(yield select(authSelectors.getAuthSession)));
    },
  );
}

export default [
  exchangeRefreshTokenActionWatcher,
  exchangeRefreshTokenSuccessActionWatcher,
  exchangeRefreshTokenFailureActionWatcher,
  retrieveOauthTokenWatcher,
  createDatasetSuccessActionWatcher,
  authSelectDatasetActionWatcher,
  getOAuthTokenFailureWatcher,
  watchPersistAuth,
];
